import type { DeviceDetails } from '../details-schemas';

import styles from './device-metrics-row.module.scss';

export type DeviceMetricsRowProps = {
  details: DeviceDetails;
};

type Metric = { label: string; value: string; unit: string; live: boolean };

/** An em dash, not a zero — a device that has never reported has no reading (US-112437 §2). */
const NO_READING = '—';

/**
 * Battery Voltage / Temperature / Water Level, read-only (US-112437 §2).
 *
 * The green dot marks a device that is actually reporting: it shows while the device is Running or
 * Off and is hidden when Disconnected, and never appears for an expired subscription (§19).
 */
export function DeviceMetricsRow({ details }: DeviceMetricsRowProps) {
  const live = !details.expired && details.effectiveStatus !== 'disconnected';

  const metrics: Metric[] = [
    {
      label: 'Battery Voltage',
      value: details.metrics.batteryVoltage?.toString() ?? NO_READING,
      unit: 'V',
      live: live && details.metrics.batteryVoltage != null,
    },
    {
      label: 'Temperature',
      value: details.metrics.temperatureF != null ? `${details.metrics.temperatureF}°` : NO_READING,
      unit: 'F',
      live: live && details.metrics.temperatureF != null,
    },
    {
      label: 'Water Level',
      value: details.metrics.waterLevelInches?.toString() ?? NO_READING,
      unit: 'Inches',
      live: live && details.metrics.waterLevelInches != null,
    },
  ];

  return (
    <section className={styles.card} aria-label="Device metrics">
      {metrics.map((metric) => (
        <div key={metric.label} className={styles.metric}>
          <p className={styles.label}>{metric.label}</p>
          <p className={styles.reading}>
            <span className={styles.value}>{metric.value}</span>
            <span className={styles.unit}>{metric.unit}</span>
            {metric.live ? <span className={styles.dot} aria-label="Reporting" /> : null}
          </p>
        </div>
      ))}
    </section>
  );
}
